/**
 * Example-word lists keyed by phonics target pattern. Used to populate the
 * `exampleWords` field on every `PhonicsObjective` so the curriculum +
 * objective landing pages can show "Example target words" chips without
 * an editorial pass per objective.
 *
 * Keys are the canonical sound notation found in the objective `value`
 * strings (e.g. `/m/`, `/sh/`, `/ai/`). Some curricula (notably UFLI) use
 * diacritic IPA letters — `/ā/`, `/ē/`, `/ī/`, `/ō/`, `/ū/`, `/ă/`, `/ĕ/`,
 * `/ĭ/`, `/ŏ/`, `/ŭ/`. Those are mapped to the same lists as the bare-vowel
 * notation other curricula use. Bluebonnet writes long-vowel CVCe as
 * `/ae/`, `/ie/`, `/oe/`, `/ue/`.
 *
 * Words are picked to be grade-appropriate so the landing-page chips are
 * authoritative for young readers (and rank for the same long-tail
 * searches parents and teachers actually run).
 */

const EXAMPLE_WORDS_BY_PATTERN: Record<string, string[]> = {
  // ---- Single consonants ----
  '/m/': ['man', 'mom', 'mat', 'map', 'met'],
  '/n/': ['net', 'nut', 'nap', 'nod', 'nip'],
  '/p/': ['pen', 'pin', 'pup', 'pot', 'pan'],
  '/t/': ['top', 'tap', 'ten', 'tin', 'tug'],
  '/d/': ['dog', 'dot', 'dip', 'dug', 'dad'],
  '/k/': ['cat', 'cup', 'cab', 'cot', 'cob'],
  '/g/': ['got', 'gap', 'gum', 'gut', 'gas'],
  '/b/': ['bat', 'bed', 'big', 'bug', 'box'],
  '/h/': ['hat', 'hop', 'hit', 'hen', 'hug'],
  '/s/': ['sit', 'sun', 'sad', 'sip', 'sat'],
  '/f/': ['fan', 'fun', 'fit', 'fox', 'fig'],
  '/v/': ['van', 'vet', 'vat', 'vow', 'vest'],
  '/z/': ['zip', 'zap', 'zen', 'zoo', 'zoom'],
  '/l/': ['lap', 'log', 'lid', 'lot', 'lip'],
  '/r/': ['rat', 'run', 'red', 'rip', 'rot'],
  '/w/': ['win', 'wet', 'web', 'wig', 'wag'],
  '/j/': ['jam', 'jet', 'job', 'jog', 'jug'],
  '/y/': ['yes', 'yet', 'yum', 'yap', 'yak'],
  '/x/': ['box', 'fix', 'fox', 'mix', 'six'],
  '/qu/': ['quit', 'quiz', 'quick', 'quack', 'queen'],
  '/ks/': ['box', 'fix', 'fox', 'mix', 'six'],
  '/kw/': ['quit', 'quiz', 'quick', 'quack', 'queen'],
  // `Letter C` lookups land on `/c/`; alias to /k/.
  '/c/': ['cat', 'cup', 'cab', 'cot', 'cob'],

  // ---- Short vowels ----
  '/a/': ['cat', 'bat', 'sat', 'man', 'pan'],
  '/e/': ['bed', 'red', 'hen', 'pen', 'leg'],
  '/i/': ['big', 'pin', 'sit', 'win', 'rip'],
  '/o/': ['top', 'pot', 'mom', 'hot', 'lot'],
  '/u/': ['cup', 'sun', 'bug', 'mud', 'nut'],

  // UFLI diacritic short-vowel notation — same lists, aliased.
  '/ă/': ['cat', 'bat', 'sat', 'man', 'pan'],
  '/ĕ/': ['bed', 'red', 'hen', 'pen', 'leg'],
  '/ĭ/': ['big', 'pin', 'sit', 'win', 'rip'],
  '/ŏ/': ['top', 'pot', 'mom', 'hot', 'lot'],
  '/ŭ/': ['cup', 'sun', 'bug', 'mud', 'nut'],

  // ---- Long vowels (diacritic IPA notation) ----
  '/ā/': ['cake', 'gate', 'name', 'tape', 'plate'],
  '/ē/': ['tree', 'bee', 'see', 'free', 'feet'],
  '/ī/': ['like', 'time', 'ride', 'side', 'five'],
  '/ō/': ['home', 'rope', 'note', 'stone', 'phone'],
  '/ū/': ['flute', 'tube', 'rude', 'June', 'rule'],
  '/yū/': ['cute', 'cube', 'mule', 'use', 'fuse'],
  '/ʊ/': ['book', 'foot', 'good', 'look', 'wood'],
  '/ə/': ['about', 'pencil', 'lemon', 'circus', 'sofa'],
  '/ough/': ['cough', 'rough', 'tough', 'though', 'through'],

  // ---- Digraphs ----
  '/ch/': ['chip', 'chin', 'chop', 'rich', 'much'],
  '/sh/': ['ship', 'shop', 'fish', 'cash', 'rash'],
  '/th/': ['this', 'that', 'them', 'thin', 'with'],
  '/ng/': ['ring', 'king', 'song', 'long', 'sing'],
  '/ŋ/': ['ring', 'king', 'song', 'long', 'sing'],
  '/ŋk/': ['pink', 'sink', 'bank', 'think', 'drink'],
  '/wh/': ['whip', 'when', 'what', 'whiz', 'whim'],
  '/ph/': ['phone', 'graph', 'phase', 'photo', 'phonics'],

  // ---- Vowel teams ----
  '/ai/': ['rain', 'mail', 'paid', 'sail', 'train'],
  '/ay/': ['day', 'say', 'way', 'play', 'stay'],
  '/ee/': ['see', 'tree', 'free', 'feet', 'need'],
  '/ea/': ['eat', 'sea', 'tea', 'read', 'meat'],
  '/oa/': ['boat', 'coat', 'goat', 'road', 'soap'],
  '/oo/': ['moon', 'soon', 'food', 'pool', 'book'],
  '/ow/': ['cow', 'how', 'now', 'down', 'town'],
  '/ou/': ['out', 'cloud', 'mouth', 'house', 'about'],
  '/oi/': ['coin', 'boil', 'oil', 'soil', 'point'],
  '/oy/': ['boy', 'toy', 'joy', 'enjoy', 'royal'],
  '/aw/': ['saw', 'paw', 'jaw', 'draw', 'lawn'],
  '/au/': ['author', 'pause', 'autumn', 'sauce', 'haunt'],
  '/ew/': ['new', 'few', 'grew', 'flew', 'blew'],
  '/igh/': ['high', 'sigh', 'night', 'light', 'right'],
  '/eigh/': ['eight', 'weight', 'sleigh', 'neighbor', 'freight'],
  '/ey/': ['key', 'they', 'obey', 'honey', 'money'],

  // ---- R-controlled ----
  '/ar/': ['car', 'far', 'bark', 'dark', 'start'],
  '/or/': ['for', 'fork', 'corn', 'short', 'sport'],
  '/er/': ['her', 'over', 'mother', 'sister', 'water'],
  '/ir/': ['bird', 'girl', 'first', 'shirt', 'stir'],
  '/ur/': ['fur', 'hurt', 'turn', 'burn', 'curl'],
  '/air/': ['air', 'hair', 'chair', 'pair', 'fair'],
  '/ear/': ['ear', 'hear', 'near', 'year', 'clear'],
  '/ore/': ['more', 'core', 'store', 'score', 'before'],

  // ---- Long vowels (CVCe — Bluebonnet writes /ae/, /ie/, /oe/, /ue/) ----
  '/ae/': ['cake', 'gate', 'name', 'tape', 'plate'],
  '/ie/': ['like', 'time', 'ride', 'side', 'five'],
  '/oe/': ['home', 'rope', 'note', 'stone', 'phone'],
  '/ue/': ['cute', 'cube', 'tube', 'mule', 'use'],

  // ---- Special UFLI patterns (silent-letter consonant pairs, trigraphs) ----
  '/kn/': ['knee', 'knot', 'knock', 'knife', 'know'],
  '/wr/': ['write', 'wrap', 'wreck', 'wrong', 'wrist'],
  '/mb/': ['lamb', 'thumb', 'climb', 'comb', 'limb'],
  '/tch/': ['catch', 'match', 'witch', 'watch', 'pitch'],
  '/dge/': ['badge', 'edge', 'fudge', 'judge', 'bridge'],
  '/gn/': ['sign', 'gnat', 'reign', 'design', 'gnaw'],

  // ---- Suffixes / inflectional endings ----
  '-ed': ['walked', 'jumped', 'helped', 'rested', 'wanted'],
  '-ing': ['running', 'jumping', 'reading', 'sitting', 'playing'],
  '-er-suf': ['faster', 'taller', 'smaller', 'bigger', 'higher'],
  '-est': ['fastest', 'tallest', 'smallest', 'biggest', 'highest'],
  '-ful': ['helpful', 'careful', 'thankful', 'joyful', 'hopeful'],
  '-less': ['hopeless', 'careless', 'restless', 'useless', 'fearless'],
  '-ly': ['quickly', 'slowly', 'happily', 'softly', 'kindly'],
  '-s': ['cats', 'dogs', 'hats', 'maps', 'pens'],
  '-es': ['boxes', 'foxes', 'dishes', 'wishes', 'classes'],
  '-y-suf': ['happy', 'lucky', 'sunny', 'funny', 'puppy'],
  '-le': ['apple', 'table', 'little', 'puddle', 'middle'],
  '-tion': ['action', 'station', 'nation', 'lotion', 'motion'],
  '-sion': ['vision', 'mission', 'session', 'mansion', 'tension'],
  '-ture': ['nature', 'picture', 'future', 'mixture', 'capture'],
  '-ish': ['fish', 'wish', 'dish', 'selfish', 'foolish'],
  '-ness': ['kindness', 'darkness', 'sadness', 'sickness', 'fitness'],
  '-ment': ['payment', 'movement', 'moment', 'basement', 'shipment'],
  '-able': ['readable', 'lovable', 'usable', 'breakable', 'enjoyable'],
  '-ible': ['visible', 'possible', 'sensible', 'terrible', 'horrible'],
  '-or-suf': ['actor', 'doctor', 'editor', 'visitor', 'sailor'],
  '-ist': ['artist', 'florist', 'tourist', 'dentist', 'pianist'],
  '-ous': ['famous', 'nervous', 'jealous', 'dangerous', 'enormous'],
  '-ive': ['active', 'native', 'massive', 'creative', 'positive'],
  '-al': ['final', 'animal', 'metal', 'normal', 'tropical'],
  '-ian': ['Martian', 'civilian', 'librarian', 'historian', 'musician'],
  '-en': ['kitten', 'broken', 'frozen', 'happen', 'oven'],

  // ---- Prefixes ----
  'un-': ['unhappy', 'unfair', 'unkind', 'unlock', 'undo'],
  're-': ['redo', 'replay', 'rewrite', 'reread', 'refill'],
  'dis-': ['dislike', 'disagree', 'disappear', 'disorder', 'discount'],
  'pre-': ['preview', 'prepay', 'pretest', 'preheat', 'pregame'],
  'mis-': ['misplace', 'misread', 'misspell', 'mistake', 'misuse'],
  'bi-': ['bike', 'bicycle', 'biweekly', 'bilingual', 'binocular'],
  'tri-': ['tricycle', 'triangle', 'triple', 'trio', 'tripod'],
  'uni-': ['unicycle', 'unicorn', 'uniform', 'universe', 'united'],
  'non-': ['nonstop', 'nonfat', 'nonsense', 'nonfiction', 'nontoxic'],
  'sub-': ['submarine', 'subway', 'submit', 'subset', 'subtract'],

  // ---- CVC / generic patterns ----
  cvc: ['cat', 'sit', 'top', 'red', 'bug'],
  'short-vowel-cvc': ['cat', 'sit', 'top', 'red', 'bug'],

  // ---- Named UFLI / general lessons that don't anchor on a single sound ----
  'flsz-rule': ['bell', 'mill', 'puff', 'hiss', 'buzz'],
  'all-oll-ull': ['ball', 'call', 'roll', 'doll', 'bull'],
  nasalized_a: ['ant', 'hand', 'sand', 'plan', 'stamp'],
  vce_review: ['cake', 'time', 'home', 'cube', 'five'],
  compound_words: ['sunset', 'backpack', 'cupcake', 'bedtime', 'popcorn'],
  open_closed: ['robot', 'tiger', 'happy', 'magnet', 'paper'],
  long_vcc: ['wild', 'cold', 'find', 'most', 'bolt'],
  drop_e_rule: ['baking', 'riding', 'hoping', 'sliding', 'smiling'],
  doubling_rule: ['running', 'sitting', 'hopped', 'planned', 'bigger'],
  y_to_i_rule: ['babies', 'cried', 'happiest', 'replied', 'tried'],
  y_long_e: ['happy', 'baby', 'lucky', 'funny', 'lady'],
  y_long_i: ['fly', 'my', 'cry', 'why', 'try'],
  signal_vowels_c: ['city', 'cent', 'face', 'rice', 'circle'],
  signal_vowels_g: ['gym', 'gem', 'page', 'cage', 'giant'],
  syllables_vowel_consonant: ['picnic', 'rabbit', 'sunset', 'napkin', 'magnet'],
  // Schwa is the unstressed vowel — examples cover both common spellings.
  schwa: ['about', 'pencil', 'lemon', 'circus', 'sofa'],
  schwa_l: ['little', 'apple', 'metal', 'pencil', 'animal'],
  // Two-syllable patterns Bluebonnet/CKLA teach as syllable division types.
  open_syllables: ['hi', 'me', 'go', 'baby', 'tiger'],
  closed_syllables: ['cat', 'dog', 'magnet', 'napkin', 'rabbit'],
  vc_cv_division: ['rabbit', 'magnet', 'picnic', 'basket', 'sunset'],
  v_cv_division: ['robot', 'tiger', 'pilot', 'silent', 'lazy'],
  // C-le family.
  consonant_le: ['apple', 'table', 'little', 'puddle', 'bubble'],
  // Soft consonants (when c/g represent /s/ or /j/).
  soft_c: ['city', 'cent', 'ice', 'race', 'place'],
  soft_g: ['gym', 'gem', 'cage', 'page', 'giant'],
  // Tricky spelling buckets — generic high-frequency reads for the letter.
  tricky_a: ['water', 'father', 'want', 'wash', 'banana'],
  tricky_e: ['her', 'pretty', 'they', 'eight', 'sergeant'],
  tricky_i: ['find', 'pizza', 'ski', 'kind', 'wild'],
  tricky_o: ['no', 'who', 'do', 'love', 'one'],
  tricky_u: ['put', 'bush', 'truth', 'busy', 'use'],
  tricky_y: ['by', 'happy', 'gym', 'yes', 'yellow'],
  tricky_c: ['city', 'cent', 'face', 'cat', 'cup'],
  tricky_s: ['his', 'sit', 'sugar', 'sure', 'busy'],
  // Bluebonnet trigraph review buckets.
  trigraph_air_are: ['air', 'hair', 'care', 'share', 'square'],
  trigraph_ear_ere: ['ear', 'hear', 'here', 'severe', 'sphere'],
  // Contractions buckets.
  contraction_nt: ["isn't", "didn't", "wasn't", "doesn't", "wouldn't"],
  contraction_re_ll_d: ["they're", "we'll", "I'd", "you'll", "they'd"],
  // Homophone bucket (pairs that sound the same).
  homophones: ['rain/reign', 'see/sea', 'mail/male', 'pail/pale', 'sail/sale'],
  // Reading-review buckets used when the lesson is purely application.
  multisyllabic: ['rabbit', 'magnet', 'picnic', 'basket', 'tiger'],
  vce_words: ['cake', 'time', 'home', 'cube', 'note'],
  long_vowel_teams: ['rain', 'see', 'boat', 'play', 'tree'],
  // Suffix-rule (Drop e / Doubling / y→i) buckets — covered by named rules.
  // Final stable syllables — common -tion / -sion / -ture endings together.
  final_stable_syllables: ['action', 'nation', 'picture', 'mansion', 'vision'],
  // Letter-cluster (kn, wr, wh) review.
  silent_letter_review: ['knee', 'write', 'wreck', 'lamb', 'sign'],
  // 2-syllable word grab bag for CKLA.
  two_syllable: ['rabbit', 'sunset', 'magnet', 'paper', 'tiger'],
  // Phoneme-manipulation Heggerty lessons — pick representative blends.
  phoneme_manipulation: ['stop', 'last', 'plan', 'frog', 'crab'],
  segment_blend: ['cat', 'dog', 'fish', 'bird', 'sun'],
};

/**
 * Try to derive five example words for a Phonics-Wizard objective value
 * string. Returns undefined when the rule is too descriptive / abstract
 * to map cleanly (e.g. "Student Performance Assessment").
 */
export function deriveExampleWords(value: string): string[] | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();

  // 1) Strip tricky-word / appended-clause suffixes ("/y/ > 'y' + Tricky Word: Yellow")
  //    Also normalise en-dashes (Bluebonnet/CKLA writes prefixes/suffixes with
  //    U+2013 — e.g. `un–`, `–ist`) to ASCII hyphens so the downstream
  //    affix matchers fire uniformly.
  const normalised = trimmed.replace(/[‐‑‒–—―]/g, '-');
  const beforePlus = normalised.split(/\s+\+\s+/)[0];

  // -- Named UFLI / Bluebonnet lessons handled BEFORE the slash/affix matchers --
  // (These match phrases that would otherwise hit the slash matcher with a
  // wrong key — e.g. `y /ē/` would try `/ē/` which is mapped to long-e
  // generic words, but `y /ē/` specifically calls for y-as-long-e words.)
  if (/^\s*y\s*\/ē\//i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['y_long_e'];
  }
  if (/^\s*y\s*\/ī\//i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['y_long_i'];
  }
  if (/^-le\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-le'];
  if (/^vce\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['vce_review'];
  if (/nasalized\s+a/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['nasalized_a'];
  }
  if (/^compound words?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['compound_words'];
  }
  if (/^open\s+and\s+closed/i.test(beforePlus) || /^closed\/closed/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['open_closed'];
  }
  if (/^long\s+vcc/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['long_vcc'];
  if (/drop\s+e\s+rule/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['drop_e_rule'];
  }
  if (/doubling\s+rule/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['doubling_rule'];
  }
  if (/y\s+to\s+i\s+rule/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['y_to_i_rule'];
  }
  if (/flsz/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['flsz-rule'];
  if (/^-all,\s*-oll,\s*-ull/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['all-oll-ull'];
  }
  // "Short Vowel Review", "Short Vowel Review (all)", "Digraphs Review N"
  if (
    /^short\s+vowel\s+review/i.test(beforePlus) ||
    /^digraphs?\s+review/i.test(beforePlus)
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // R-controlled / Vowel-team review buckets — give a representative mix.
  if (/^r-?controlled.*review/i.test(beforePlus)) {
    return ['car', 'her', 'bird', 'fork', 'turn'];
  }
  if (/^vowel\s+teams?\s+review/i.test(beforePlus)) {
    return ['rain', 'see', 'boat', 'play', 'tree'];
  }
  if (/^affixes?\s+review/i.test(beforePlus)) {
    return ['unhappy', 'replay', 'jumping', 'helpful', 'quickly'];
  }
  if (/^ending\s+patterns\s+review/i.test(beforePlus)) {
    return ['catch', 'badge', 'apple', 'wild', 'cold'];
  }
  if (/(tch|dge).*review/i.test(beforePlus)) {
    return ['catch', 'badge', 'witch', 'fudge', 'pitch'];
  }
  if (/diphthongs?\s+review/i.test(beforePlus)) {
    return ['boy', 'coin', 'cow', 'out', 'toy'];
  }
  // Schwa / consonant-le family.
  if (/\bschwa\b|\/ə\//i.test(beforePlus)) {
    if (
      /\+\s*\/l\//i.test(beforePlus) ||
      /\bel\b|\ble\b|\bal\b|\bil\b|\bul\b/i.test(beforePlus)
    ) {
      return EXAMPLE_WORDS_BY_PATTERN['schwa_l'];
    }
    return EXAMPLE_WORDS_BY_PATTERN['schwa'];
  }
  if (
    /consonant-le|-ble|-dle|-gle|-cle|-kle|-ckle|-ple|-sle|-zle|-cal\b/i.test(beforePlus)
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['consonant_le'];
  }
  // Soft c / soft g.
  if (/soft\s+c\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['soft_c'];
  if (/soft\s+g\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['soft_g'];
  // Syllable-division patterns.
  if (/\bopen\s+syllables?\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['open_syllables'];
  }
  if (/\bclosed\s+syllables?\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['closed_syllables'];
  }
  if (/^vc\/cv|^vcccv|^vc\/v\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['vc_cv_division'];
  }
  if (/^v\/cv\b|^vcv\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['v_cv_division'];
  }
  // Tricky spelling buckets (Bluebonnet/CKLA naming convention).
  const trickyMatch = beforePlus.match(
    /tricky\s+(?:spelling|word\s*:?)?\s*['']?([aeiouyACGSacgs])/i,
  );
  if (trickyMatch) {
    const ch = trickyMatch[1].toLowerCase();
    const key = `tricky_${ch}`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // Bluebonnet trigraph review groups.
  if (/(air|are).*(air|are)/i.test(beforePlus) && /tri|review/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['trigraph_air_are'];
  }
  if (/(ear|ere).*(ear|ere)/i.test(beforePlus) && /tri|review/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['trigraph_ear_ere'];
  }
  // Contractions.
  if (/contractions?\b.*\bn'?t\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['contraction_nt'];
  }
  if (/contractions?\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['contraction_re_ll_d'];
  }
  // Homophones / homographs.
  if (/homophones?|homographs?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['homophones'];
  }
  // Long vowel teams review.
  if (/long\s+vowel\s+teams?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['long_vowel_teams'];
  }
  // Final stable syllables.
  if (/final\s+stable\s+syllables?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['final_stable_syllables'];
  }
  // Suffix Rules (named rule rollups).
  if (/suffix\s+rules?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['doubling_rule'];
  }
  // -ous / -ive / -al / -ian / -en endings (not already in suffix regex).
  if (/^-(ous|ive|al|ian|en)\b/i.test(beforePlus)) {
    const m = beforePlus.match(/^-(ous|ive|al|ian|en)\b/i)!;
    return EXAMPLE_WORDS_BY_PATTERN[`-${m[1].toLowerCase()}`];
  }
  // Plain -y / -es bare-suffix entries (UFLI 99, 123).
  if (/^-y\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-y-suf'];
  if (/^-es\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-es'];
  // Bare prefix entries (Bluebonnet/CKLA use these as their own rows).
  const bareprefix = beforePlus.match(
    /^(un|re|dis|pre|mis|bi|tri|uni|non|sub|pro|anti|in|im)-/i,
  );
  if (bareprefix) {
    const key = `${bareprefix[1].toLowerCase()}-`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
    return EXAMPLE_WORDS_BY_PATTERN['un-']; // generic prefix fallback
  }
  // Bare suffix entries (Bluebonnet/CKLA).
  const baresuffix = beforePlus.match(
    /^-(ist|ous|ive|al|ian|en|ful|less|ly|tion|sion|ture|ish|ness|ment|able|ible|ed|ing|est)\b/i,
  );
  if (baresuffix) {
    const key = `-${baresuffix[1].toLowerCase()}`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // Specific Bluebonnet/UFLI review buckets.
  if (/^ck\s+review/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/c/'];
  if (/^oo\s+review/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/oo/'];
  if (/^wh,\s*sh,\s*th/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  if (/^ar,\s*or,\s*ore/i.test(beforePlus)) {
    return ['car', 'fork', 'more', 'corn', 'before'];
  }
  if (/^ough\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/ough/'];
  // CKLA's tricky-quote spelling syntax: "'a' tricky", "'i' tricky review".
  const trickyQuote = beforePlus.match(/^['']?([aeiouy])['']?\s+tricky/i);
  if (trickyQuote) {
    const key = `tricky_${trickyQuote[1].toLowerCase()}`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // CKLA "Suffixes after VCe (drop e)" / "Vowel Suffixes (drop e)".
  if (/\(drop\s+e\)/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['drop_e_rule'];
  // "Long vowel sounds in VCe context" → VCe words.
  if (/long\s+vowel.*vce|vce.*long\s+vowel|magic-?e/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['vce_words'];
  }
  // CKLA "Review vowel sounds" / "Review vowel digraphs" / "Vowel digraph spellings review"
  if (/review\s+vowel\b|vowel\s+(digraphs?|code)/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['long_vowel_teams'];
  }
  // CKLA "Review consonant + vowel sounds", "Demonstration Story",
  // "Partner Reading", "Small Group Reading", "Whole Group Reading",
  // "Practice reading words", "Reading Wiggle Cards", "Uppercase letters",
  // "Letter Names", "Alphabet" — pedagogy lessons get the CVC bucket.
  if (
    /demonstration\s+story|partner\s+reading|small\s+group|whole\s+group|practice\s+reading|wiggle\s+cards|alphabet|letter\s+names?|uppercase|consonant.*vowel|introduction|review\s+rhyming|review\s+digraphs|double[-\s]letter|double\s+letters|root\s+words|introduction\s+to\s+phrases|long\s+vs\s+short|long-?vowel\s+dictation/i.test(
      beforePlus,
    )
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // Bluebonnet "Writing", "Grammar", "Spelling: …" pure-pedagogy rows
  // (none of these are decoding lessons; example chips are fine to suppress
  // by leaving them undefined — see the disable review).
  // Bluebonnet "Spelling Assessment (review) + Suffix -tion intro"
  if (/suffix\s*-?tion/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-tion'];
  // Bluebonnet "Prefixes un–, re–, dis–"
  if (/^prefixes?\b/i.test(beforePlus)) {
    const first = beforePlus.match(
      /(un|re|dis|pre|mis|bi|tri|uni|non|sub|pro|anti|in|im)-/i,
    );
    if (first) {
      const key = `${first[1].toLowerCase()}-`;
      if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
    }
    return EXAMPLE_WORDS_BY_PATTERN['un-'];
  }
  // Plural assessment / generic suffix lessons → -s/-es.
  if (/plural\s+nouns?|plural\s+markers?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-s'];
  }
  // -ed, -ing as a bare lesson without "suffix" prefix.
  if (/^-ed,\s*-ing\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-ing'];
  // -ful, -less / -able, -ible / -ness, -y, -ful — multi-suffix titles.
  if (/^-ful,\s*-less\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-ful'];
  if (/^-able,\s*-ible/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-able'];
  if (/^-ness,\s*-y,?\s*-ful/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-ness'];
  if (/abbreviations?/i.test(beforePlus)) return ['Mr.', 'Dr.', 'St.', 'Ave.', 'Jr.'];
  // "Single-Syllable, Short-Vowel Words" / "Short Vowel Discrimination"
  if (
    /short[-\s]vowel|short\s+vowel\s+discrimination|single-?syllable/i.test(beforePlus)
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // CKLA tricky-spelling-with-/k/-alts
  if (/tricky\s+spelling\s+['']?g['']?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['soft_g'];
  }
  if (/tricky\s+spelling\s+['']?c['']?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['soft_c'];
  }
  if (/spelling\s+alts/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['silent_letter_review'];
  }
  // CKLA "Medial sounds", "Minimal pairs" → CVC.
  if (/medial\s+sounds?|minimal\s+pairs?|clapping/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // HMH bare single-letter values ("m", "s", "t", "a", …).
  const bareLetter = beforePlus.match(/^([a-zA-Z])$/);
  if (bareLetter) {
    const key = `/${bareLetter[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // Wonders / generic "(spelled X)" parens hint.
  const spelledMatch = beforePlus.match(/\(spelled\s+([^)]+)\)/i);
  if (spelledMatch) {
    const first = spelledMatch[1]
      .split(/[,;]\s*/)[0]
      .trim()
      .toLowerCase();
    // a_e, i_e, o_e, u_e, e_e — CVCe family.
    if (/^[aeiou]_e$/.test(first)) {
      const v = first[0];
      const key = `/${v}e/`;
      if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
    }
    const slashKey = `/${first}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[slashKey]) return EXAMPLE_WORDS_BY_PATTERN[slashKey];
  }
  // Wonders "long vowel a (a_e)", "long a (spelled a_e)", "long a (a, ai, ay)"
  const longVowelMatch = beforePlus.match(/long\s+(?:vowel\s+)?([aeiou])\b/i);
  if (longVowelMatch) {
    const v = longVowelMatch[1].toLowerCase();
    const candidates = [`/${v}e/`, `/${v === 'a' ? 'ai' : v + 'e'}/`];
    for (const c of candidates) {
      if (EXAMPLE_WORDS_BY_PATTERN[c]) return EXAMPLE_WORDS_BY_PATTERN[c];
    }
  }
  // Wonders "r-controlled vowel (ar)", "r-controlled vowels (or, ir, ur, er)".
  if (/r-?controlled/i.test(beforePlus)) {
    const inParen = beforePlus.match(/\((ar|or|ir|ur|er|air|are|ear|ore|oar)/i);
    if (inParen) {
      const key = `/${inParen[1].toLowerCase()}/`;
      if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
    }
    return ['car', 'her', 'bird', 'fork', 'turn'];
  }
  // Wonders "diphthongs (ou, ow)" / "diphthongs (oi, oy)".
  if (/diphthongs?/i.test(beforePlus)) {
    if (/\bou\b|\bow\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/ou/'];
    if (/\boi\b|\boy\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/oi/'];
    return ['boy', 'coin', 'cow', 'out', 'toy'];
  }
  // Wonders "consonant digraphs (sh, th, -ng)" / "(ch, -tch, wh, ph)".
  if (/consonant\s+digraphs?/i.test(beforePlus)) {
    if (/\bsh\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/sh/'];
    if (/\bch\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/ch/'];
    return EXAMPLE_WORDS_BY_PATTERN['/sh/'];
  }
  if (/variant\s+vowel/i.test(beforePlus)) {
    if (/\boo\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/oo/'];
    if (/\baw\b|\bau\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/aw/'];
    return EXAMPLE_WORDS_BY_PATTERN['/oo/'];
  }
  if (/letter\s+recognition/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // Wilson "Glued sound -all" / "Glued sounds am/an" / "-ng/-nk".
  if (/glued\s+sound[s]?/i.test(beforePlus)) {
    if (/-all\b|all\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['all-oll-ull'];
    if (/am\/an|am, an|am or an/i.test(beforePlus)) {
      return EXAMPLE_WORDS_BY_PATTERN['nasalized_a'];
    }
    if (/-ng|-nk|ng\/nk/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/ng/'];
    return EXAMPLE_WORDS_BY_PATTERN['/ng/'];
  }
  // Wilson "Decode/spell <pattern>" - delegate to the inner pattern.
  // VCe family.
  if (
    /\bvce\s+(hfw|words?|exception)/i.test(beforePlus) ||
    /\bvce\s+context/i.test(beforePlus)
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['vce_words'];
  }
  // ai/ay, oi/oy, etc. slash-shorthand vowel-team pairs.
  const pairMatch = beforePlus.match(
    /\b(ai|ay|ee|ea|oa|oo|ow|ou|oi|oy|aw|au|ew|ar|or|er|ir|ur)\/(ai|ay|ee|ea|oa|oo|ow|ou|oi|oy|aw|au|ew|ar|or|er|ir|ur)\b/i,
  );
  if (pairMatch) {
    const key = `/${pairMatch[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // Wilson "Long a/e vowel teams" / "Long o/u vowel teams" / "Long a vowel teams"
  if (/long\s+[aeiou](?:\/[aeiou])?\s+(?:vowel\s+team|vowel-?team)/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['long_vowel_teams'];
  }
  // Wilson generic syllable division, vowel team syllable type, r-controlled syllable
  if (/syllable\s+division|syllable\s+type|vowel-?team\s+syllable/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['vc_cv_division'];
  }
  // Wilson "Suffix -s on base words" / "Pluralization with -s" / "Decode/spell -s plural words"
  if (/suffix\s+-s\b|pluralization|plural\s+formation/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-s'];
  }
  // Wilson "Comparative & superlative suffixes"
  if (/comparative.*superlative|superlative.*comparative/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-er-suf'];
  }
  // Wilson "Two sounds of s"
  if (/two\s+sounds\s+of\s+s/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/s/'];
  // Wilson "y functioning as a vowel"
  if (/y\s+functioning|y\s+as\s+a?\s+vowel|y\s+vowel/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['y_long_e'];
  }
  // Wilson "-ic spelling at word end"
  if (/-ic\s+spelling/i.test(beforePlus)) {
    return ['picnic', 'magic', 'topic', 'panic', 'comic'];
  }
  // Wilson generic "Decode/spell <something> HFW" or "<something> words"
  if (
    /\bhfw\b|high-?frequency\s+words?/i.test(beforePlus) ||
    /trick\s+words?/i.test(beforePlus)
  ) {
    return ['the', 'was', 'said', 'you', 'they'];
  }
  // Wilson generic "Decode/spell <something> words" — fall back to CVC.
  if (/decode|spell|read/i.test(beforePlus) && !/-/.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // CKLA multi-char tricky-quote ("'oo' tricky", "'o_e' tricky", "'ou' tricky").
  const trickyMulti = beforePlus.match(/^['"]?([a-z]+(?:_[a-z]+)?)['"]?\s+tricky/i);
  if (trickyMulti) {
    const inner = trickyMulti[1].toLowerCase();
    const slash = `/${inner.includes('_') ? inner.replace('_', '') : inner}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[slash]) return EXAMPLE_WORDS_BY_PATTERN[slash];
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // "Review /tion/" — slash matcher should have caught it but the regex
  // demands the slash chars stick to the keyword.  Re-handle.
  const reviewSlash = beforePlus.match(/review\s+\/([a-zĀĒĪŌŪăĕĭŏŭāēīōūəʊ_]+)\//i);
  if (reviewSlash) {
    const key = `/${reviewSlash[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // Heggerty / CKLA generic phoneme-manipulation lessons.
  if (
    /segment|blend|isolate|substitut|delete|onset|rime|chaining|manipulat/i.test(
      beforePlus,
    )
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['phoneme_manipulation'];
  }
  // Two-syllable / multisyllabic decoding / spelling.
  if (/2-syllable|two-?syllable|multisyllabic|3-syllable/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['two_syllable'];
  }
  // Generic silent-letter review.
  if (/silent\s+letter|kn,?\s*wr,?\s*(?:wh|mb)/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['silent_letter_review'];
  }
  // "Suffixes; -s/-es", "Suffixes -s and -es"
  if (
    /^suffixes\b.*-s(?:[\/,]|\s+and\s+)-es/i.test(beforePlus) ||
    /^-s\b/i.test(beforePlus)
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['-s'];
  }
  if (/signal\s+vowels.*\bc\b\s*\/s\//i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['signal_vowels_c'];
  }
  if (/signal\s+vowels.*\bg\b\s*\/j\//i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['signal_vowels_g'];
  }

  // 2) `/<sound>/` notation. Diacritic IPA characters are common in UFLI —
  //    we accept any non-`/`, non-whitespace chars between the slashes, then
  //    look the key up case-insensitively.
  const slashMatch = beforePlus.match(/\/([^/\s]+)\//);
  if (slashMatch) {
    const inner = slashMatch[1];
    const candidates = [`/${inner.toLowerCase()}/`, `/${inner}/`];
    for (const key of candidates) {
      if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
    }
  }

  // 3) Prefix marker ("Prefixes un- and re-", "un-", etc.)
  const prefixMatch = beforePlus.match(/\b(un|re|dis|pre|mis|bi|tri|uni|non|sub)-/i);
  if (prefixMatch) {
    const key = `${prefixMatch[1].toLowerCase()}-`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }

  // 4) Suffix markers ("suffix -er and -est", "-ing", "-ed", etc.)
  const suffixMatch = beforePlus.match(
    /-(ed|ing|est|ful|less|ly|tion|sion|ture|ish|ness|ment|able|ible)\b/i,
  );
  if (suffixMatch) {
    const raw = suffixMatch[1].toLowerCase();
    const key = `-${raw}`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // -er, -or, -y, -ist collide with sound keys, so disambiguate by phrase.
  if (/\bsuffix(?:es)?\b[^-]*-er\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-er-suf'];
  }
  if (/\bsuffix(?:es)?\b[^-]*-or\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-or-suf'];
  }
  if (/\bsuffix(?:es)?\b[^-]*-y\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-y-suf'];
  }
  if (/\bsuffix(?:es)?\b[^-]*-ist\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-ist'];
  }
  if (/^-er,\s*-est/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-er-suf'];
  if (/^-er,\s*-or,\s*-ist/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-or-suf'];
  }

  // 5) Bare letter form used by UFLI ("a /ă/", "Letter A", "Short e in CVC Words")
  const shortVowelMatch = beforePlus.match(/short\s+vowel\s+(?:\/)?([aeiou])(?:\/)?/i);
  if (shortVowelMatch) {
    const key = `/${shortVowelMatch[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // "Short A Review", "Short A Advanced Review"
  const shortVowelReview = beforePlus.match(/^short\s+([aeiou])\b/i);
  if (shortVowelReview) {
    const key = `/${shortVowelReview[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  const letterMatch = beforePlus.match(/^Letter\s+([A-Za-z])$/);
  if (letterMatch) {
    const letter = letterMatch[1].toLowerCase();
    if (EXAMPLE_WORDS_BY_PATTERN[`/${letter}/`]) {
      return EXAMPLE_WORDS_BY_PATTERN[`/${letter}/`];
    }
  }

  // 6) Generic CVC / short-vowel-word fallback for entries like
  //    "Spell one-syllable, short-vowel words" or "CVC Words".
  if (/cvc/i.test(beforePlus) || /one-syllable.*short-vowel/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }

  // 7) Bare "Syllables" framing — broad multisyllabic decoding.
  if (/^syllables?$/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['syllables_vowel_consonant'];
  }

  // 8) Re-check against the full (post-normalisation) string for lessons
  //    whose meaningful keyword sits AFTER a ` + ` separator that step 1
  //    stripped. This rescues entries like
  //    "Review consonant + vowel sounds" and
  //    "Tricky Word: Fort McHenry + /ə/ alts".
  const full = normalised;
  if (/\/ə\/|schwa/i.test(full)) return EXAMPLE_WORDS_BY_PATTERN['schwa'];
  if (/\/tion\//i.test(full)) return EXAMPLE_WORDS_BY_PATTERN['-tion'];
  if (/consonant.*vowel|vowel.*consonant/i.test(full)) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }

  // 9) HMH bare-digraph / bare-trigraph values like "qu", "ck", "sh",
  //    "tch", "kn and wr", "ai and ay", "er, ir, ur".
  const bareDigraph = beforePlus.match(/^([a-z]{2,4})$/i);
  if (bareDigraph) {
    const key = `/${bareDigraph[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // "X and Y" / "X, Y, Z" lists of digraphs/vowel-teams - take the first.
  const listFirst = beforePlus.match(/^([a-z]{1,4})(?:\s+and\s+|,\s*)[a-z, ]+$/i);
  if (listFirst) {
    const key = `/${listFirst[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[key]) return EXAMPLE_WORDS_BY_PATTERN[key];
  }
  // "ending s" / "ending -s" → -s.
  if (/^ending\s+-?s\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-s'];
  if (/^possessives?\b/i.test(beforePlus)) {
    return ["dog's", "boy's", "girl's", "cat's", "mom's"];
  }
  if (/^open\s+vowels?\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['open_syllables'];
  }
  if (/^final\s+st\s+and\s+nd\b/i.test(beforePlus)) {
    return ['fast', 'last', 'hand', 'sand', 'land'];
  }
  if (/^ss,?\s*ff,?\s*ll,?\s*zz\b/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['flsz-rule'];
  }
  // Wilson "wh, ch, sh, th, ck" / "Introduce digraphs ..."
  if (
    /^wh,\s*ch,\s*sh,\s*th/i.test(beforePlus) ||
    /digraphs?\b.*wh.*ch.*sh.*th/i.test(beforePlus)
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['/sh/'];
  }
  if (/cumulative\s+(?:pa\s+)?(?:practice|review)/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  if (/\bpa\b\s*:|phonemic\s+awareness|alliteration|rhyme/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['segment_blend'];
  }
  if (/letter-?keyword-?sound|recognize\s+sound|letter\s+keyword/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  }
  // Wilson "Combine closed + VCe in 2-syllable words" / "Final -et spelling"
  if (/closed\s*\+\s*vce|combine\s+closed/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['vce_words'];
  }
  if (/^final\s+-?et\b/i.test(beforePlus)) {
    return ['target', 'planet', 'magnet', 'pocket', 'rocket'];
  }
  // Wilson "Spelling -le pattern" / "Decode/spell -le words" / "Suffixes added to c-le"
  if (/-le\s+(pattern|words?)/i.test(beforePlus) || /c-?le\s+bases?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-le'];
  }
  // Wilson "-ge / -dge endings" / "Silent e to keep c/g soft"
  if (/-ge\b.*-dge|-dge\b.*-ge/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['/dge/'];
  }
  if (/silent\s+e.*soft|soft.*silent\s+e/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['vce_words'];
  }
  if (/-ve\b|english\s+words\s+don'?t\s+end\s+in\s+v/i.test(beforePlus)) {
    return ['have', 'give', 'love', 'move', 'live'];
  }
  if (/drop\s+silent\s+e/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['drop_e_rule'];
  }
  if (/y[\-→\s]+to[\-→\s]+i/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['y_to_i_rule'];
  }
  if (/voiced\s+s\b/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/z/'];
  if (/oi\s+medial.*oy\s+(at|end)/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['/oi/'];
  }
  if (/au\s+medial.*aw\s+(at|end)/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['/aw/'];
  }
  if (/doubled\s+r\s+exception/i.test(beforePlus)) {
    return ['carry', 'marry', 'sorry', 'hurry', 'mirror'];
  }
  if (/(less\s+common|less-common)\s+vowel\s+teams/i.test(beforePlus)) {
    return ['piece', 'field', 'yield', 'chief', 'belief'];
  }
  if (/vowel\s+team\s+exceptions?/i.test(beforePlus)) {
    return ['said', 'head', 'bread', 'great', 'break'];
  }
  if (/latin\s+(prefix|root)/i.test(beforePlus)) {
    return ['transport', 'export', 'import', 'export', 'inspect'];
  }
  if (/greek-?origin/i.test(beforePlus)) {
    return ['photo', 'graph', 'phone', 'phonics', 'biology'];
  }
  if (/prefix.*root|root.*prefix|prefix.*morpholog/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['un-'];
  }
  if (/suffix.*-(ward|or)\b|-ward.*-or/i.test(beforePlus)) {
    return ['forward', 'backward', 'editor', 'doctor', 'actor'];
  }
  if (/irregular\s+plural/i.test(beforePlus)) {
    return ['children', 'men', 'women', 'mice', 'feet'];
  }
  if (/plural\s+rules?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-es'];
  }
  if (
    /vowel\s+suffix|consonant\s+suffix|suffix\s+attachment|review\s+baseline\s+suffix/i.test(
      beforePlus,
    )
  ) {
    return EXAMPLE_WORDS_BY_PATTERN['-ing'];
  }
  if (/present.*past.*tense|past.*present.*tense/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-ed'];
  }
  // RGR / HMH wrap-up patterns that the earlier matchers don't catch.
  if (/^ck$/i.test(beforePlus)) {
    return ['back', 'rock', 'duck', 'sick', 'pick'];
  }
  if (/^digraph\s+([a-z]{2,3})\b/i.test(beforePlus)) {
    const m = beforePlus.match(/^digraph\s+([a-z]{2,3})\b/i)!;
    const k = `/${m[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[k]) return EXAMPLE_WORDS_BY_PATTERN[k];
  }
  if (/^digraphs?\s+([a-z]{2,3})\s+and\s+([a-z]{2,3})\b/i.test(beforePlus)) {
    const m = beforePlus.match(/^digraphs?\s+([a-z]{2,3})\s+and\s+([a-z]{2,3})\b/i)!;
    const k = `/${m[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[k]) return EXAMPLE_WORDS_BY_PATTERN[k];
  }
  if (/^trigraphs?\s+([a-z]{2,4})\b/i.test(beforePlus)) {
    const m = beforePlus.match(/^trigraphs?\s+([a-z]{2,4})\b/i)!;
    const k = `/${m[1].toLowerCase()}/`;
    if (EXAMPLE_WORDS_BY_PATTERN[k]) return EXAMPLE_WORDS_BY_PATTERN[k];
  }
  // "Suffix -es", "Suffix -ed", "Suffix er and est" - any "Suffix <thing>"
  const suffixHeadMatch = beforePlus.match(/^suffix(?:es)?\s+-?([a-z]+(?:\s|,)*)/i);
  if (suffixHeadMatch) {
    // Walk every token in the title; first one that matches a suffix key wins.
    const tokens = beforePlus
      .toLowerCase()
      .replace(/^suffix(?:es)?\s+/i, '')
      .split(/[\s,]+/)
      .filter(Boolean);
    for (const tok of tokens) {
      const k = `-${tok.replace(/^-/, '')}`;
      if (EXAMPLE_WORDS_BY_PATTERN[k]) return EXAMPLE_WORDS_BY_PATTERN[k];
    }
  }
  // "Prefix un and re", "Prefix un, re, dis"
  if (/^prefix(?:es)?\b/i.test(beforePlus)) {
    const tokens = beforePlus
      .toLowerCase()
      .replace(/^prefix(?:es)?\b/i, '')
      .split(/[\s,]+/)
      .filter(Boolean);
    for (const tok of tokens) {
      const k = `${tok.replace(/-$/, '')}-`;
      if (EXAMPLE_WORDS_BY_PATTERN[k]) return EXAMPLE_WORDS_BY_PATTERN[k];
    }
    return EXAMPLE_WORDS_BY_PATTERN['un-'];
  }
  // "two syllable words" / "Nonsense Words"
  if (/two[- ]?syllable\s+words?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['two_syllable'];
  }
  if (/nonsense\s+words?/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['cvc'];
  // Wilson cumulative-review / continued-PA fallbacks.
  if (/cumulative.*suffix/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-ing'];
  if (/continued\s+pa\b/i.test(beforePlus))
    return EXAMPLE_WORDS_BY_PATTERN['segment_blend'];
  if (/c-?le\s+exceptions?/i.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['-le'];
  if (/3[- ]?sound\s+words.*digraphs?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['/sh/'];
  }
  if (/-ng\b|-nk\b/.test(beforePlus)) return EXAMPLE_WORDS_BY_PATTERN['/ng/'];
  if (/add\s+-s\s+to|-s\s+plural\s+words/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['-s'];
  }
  // RGR "ang, ing, ong, ung, ank, ink, onk, unk" — glued-sound nasal.
  if (/^ang,?\s*ing,?\s*ong/i.test(beforePlus)) {
    return ['sang', 'ring', 'long', 'rung', 'thank'];
  }
  // RGR "Odd Syllables: tion, sion, ture, and cious"
  if (/odd\s+syllables?/i.test(beforePlus)) {
    return EXAMPLE_WORDS_BY_PATTERN['final_stable_syllables'];
  }

  return undefined;
}
